import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import Geocode from "react-geocode"
import styled from "styled-components"
import * as Yup from "yup"

import BottomSheet from "../../../../_shared/components/BottomSheet"
import AppSwitch from "../../../../_shared/components/FormItems/AppSwitch"
import AppTextArea from "../../../../_shared/components/FormItems/AppTextarea"
import Checkbox from "../../../../_shared/components/FormItems/Checkbox"
import FormInput from "../../../../_shared/components/FormItems/FormInput"
import QuantitySelector from "../../../../_shared/components/FormItems/QuantitySelector"
import RoomImagePicker from "../../../../_shared/components/RoomImagePicker"
import useFormikForm from "../../../../_shared/hooks/useFormikForm"

// ----------------------------------------------------------------------------

function _RoomsBottomSheet(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className, open, onConfirm, onCancel, defaultValues, onDelete } =
        props

    // -------------------------------------
    // Hooks (e.g. useState, ...)
    // -------------------------------------

    const [downloadingCoords, setDownloadingCoords] = useState(false)

    const initialValues = {
        title: "",
        rent: null,
        description: "",
        images: [],
        address: {
            city: "",
            address: "",
            region: "",
            zipCode: "",
            state: "Italia",
        },
        attributes: {
            surfaceArea: null,
            floor: null,
            apartmentSurfaceArea: null,
            maxTenants: 1,
            numRooms: 1,
            hasWifi: false,
            hasGarden: false,
            hasOwnParkingOptions: false,
            hasNearbyParkingOptions: false,
            hasWashingMachine: false,
            hasTv: false,
            hasCentralHeating: false,
            hasOwnBathroom: false,
            isCloseToTrainStation: false,
            isCloseToBusLines: false,
            isCloseToHighwayExit: false,
            minStay: null,
            ownerPhoneNumber: "",
            ownerEmailAddress: "",
        },
        tags: [],
        location: {
            type: "Point",
            coordinates: [0, 0],
        },
    }

    const validationSchema = new Yup.ObjectSchema({
        title: Yup.string().required("Campo obbligatorio"),
        description: Yup.string().required("Campo obblgiatorio"),
        images: Yup.array().min(1, "Si deve selezionare almeno un'immagine"),

        rent: Yup.number()
            .nullable()
            .typeError("Il valore deve essere un numero")
            .required("Campo obbligatorio"),
        address: Yup.object({
            address: Yup.string().required("Campo obbligatorio"),
            city: Yup.string().required("Campo obbligatorio"),
            zipCode: Yup.string().required("Campo obbligatorio"),
            region: Yup.string().required("Campo obbligatorio"),
        }),
        attributes: Yup.object({
            floor: Yup.number()
                .typeError("Il valore deve essere un numero")
                .required("Campo obbligatorio"),
            surfaceArea: Yup.number()
                .nullable()
                .typeError("Il valore deve essere un numero")
                .required("Campo obbligatorio"),
            apartmentSurfaceArea: Yup.number()
                .nullable()
                .typeError("Il valore deve essere un numero")
                .required("Campo obbligatorio"),
            minStay: Yup.number()
                .nullable()
                .typeError("Il valore deve essere un numero")
                .required("Campo obbligatorio"),
            maxTenants: Yup.number().required("Campo obbligatorio"),
            numRooms: Yup.number().required("Campo obbligatorio"),
            hasWifi: Yup.bool(),
            hasGarden: Yup.bool(),
            hasOwnParkingOptions: Yup.bool(),
            hasNearbyParkingOptions: Yup.bool(),
            hasWashingMachine: Yup.bool(),
            hasAirConditioning: Yup.bool(),
            hasOwnBathroom: Yup.bool(),
            hasTv: Yup.bool(),
            hasCentralHeating: Yup.bool(),
            isCloseToTrainStation: Yup.bool(),
            isCloseToBusLines: Yup.bool(),
            isCloseToHighwayExit: Yup.bool(),
            ownerPhoneNumber: Yup.string().nullable(),
            ownerEmailAddress: Yup.string().nullable(),
        }),
    })

    const { values, getFieldProps, reset, bulkChange, handleSubmit, onChange } =
        useFormikForm({
            initialValues,
            validationSchema,
            handleSubmit: onConfirm,
        })

    // -------------------------------------
    // Memoized values
    // -------------------------------------

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        defaultValues?.id && bulkChange({ ...values, ...defaultValues })
        // eslint-disable-next-line
    }, [defaultValues])

    useEffect(() => {
        !open && reset()
        // eslint-disable-next-line
    }, [open])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function setAddressCoordinates() {
        const { address } = values
        const { address: line, city, zipCode, region } = address
        const addressText = `${line}, ${city}, ${zipCode}, ${region}, Italia`
        setDownloadingCoords(true)
        Geocode.fromAddress(addressText)
            .then((response) => {
                const lat = response.results[0].geometry.location.lat
                const lng = response.results[0].geometry.location.lng

                onChange("location.coordinates")([lng, lat])
            })
            .finally(() => setDownloadingCoords(false))
    }

    function getAddressFieldProps(field) {
        const formikProps = getFieldProps(field)
        const onBlur = () => {
            formikProps.onBlur()
            setAddressCoordinates()
        }
        return { ...formikProps, onBlur }
    }

    function renderLeft() {
        return (
            <div className="left">
                <RoomImagePicker
                    // value={form?.images}
                    // onChange={updateForm("images")}
                    {...getFieldProps("images")}
                    className="field image-picker"
                />
                <AppSwitch
                    className="field switch"
                    label="Visibile"
                    {...getFieldProps("visible")}
                />
                <FormInput
                    className="field"
                    {...getFieldProps("title")}
                    label="Titolo"
                />
                <AppTextArea
                    {...getFieldProps("description")}
                    label="Descrizione"
                    className="field"
                />

                <FormInput
                    label="Superifice stanza (mq)"
                    {...getFieldProps("attributes.surfaceArea")}
                    className="field"
                />
                <FormInput
                    label="Superifice appartamento (mq)"
                    {...getFieldProps("attributes.apartmentSurfaceArea")}
                    className="field"
                />
                <FormInput
                    label="Prezzo mensile (€)"
                    {...getFieldProps("rent")}
                    className="field"
                />
                <FormInput
                    label="Soggiorno minimo (mesi)"
                    {...getFieldProps("attributes.minStay")}
                    className="field"
                />
                <FormInput
                    label="Piano"
                    {...getFieldProps("attributes.floor")}
                    className="field"
                />
                <FormInput
                    label="Indirizzo mail proprietario"
                    {...getFieldProps("attributes.ownerEmailAddress")}
                    className="field"
                />
                <FormInput
                    label="Numero di telefono proprietario"
                    {...getFieldProps("attributes.ownerPhoneNumber")}
                    className="field"
                />
            </div>
        )
    }

    function renderRight() {
        return (
            <div className="right">
                <h3 className="heading">Indirizzo</h3>
                <FormInput
                    label="Via e numero civico"
                    className="field"
                    {...getAddressFieldProps("address.address")}
                />
                <FormInput
                    {...getAddressFieldProps("address.city")}
                    label="Città"
                    className="field"
                />
                <FormInput
                    {...getAddressFieldProps("address.zipCode")}
                    label="Codice postale"
                    className="field"
                />
                <FormInput
                    label="Provincia"
                    {...getAddressFieldProps("address.region")}
                    className="field"
                />
                <h3 className="heading">Capienza</h3>
                <QuantitySelector
                    label="Capienza massima dell’appartamento"
                    {...getFieldProps("attributes.maxTenants")}
                    className="field quantity"
                />

                <QuantitySelector
                    label="Camere totali"
                    {...getFieldProps("attributes.numRooms")}
                    className="field quantity"
                />

                <h3 className="heading">Servizi</h3>
                <Checkbox
                    label="Giardino"
                    className="field checkbox"
                    {...getFieldProps("attributes.hasGarden")}
                />
                <Checkbox
                    label="Wifi"
                    className="field checkbox"
                    {...getFieldProps("attributes.hasWifi")}
                />
                <Checkbox
                    label="Posto auto / Autorimessa"
                    className="field checkbox"
                    {...getFieldProps("attributes.hasOwnParkingOptions")}
                />
                <Checkbox
                    label="Parcheggio nelle vicinanze"
                    className="field checkbox"
                    {...getFieldProps("attributes.hasNearbyParkingOptions")}
                />
                <Checkbox
                    label="Lavatrice"
                    className="field checkbox"
                    {...getFieldProps("attributes.hasWashingMachine")}
                />
                <Checkbox
                    label="Aria condizionata"
                    className="field checkbox"
                    {...getFieldProps("attributes.hasAirConditioning")}
                />
                <Checkbox
                    label="Televisione"
                    className="field checkbox"
                    {...getFieldProps("attributes.hasTv")}
                />
                <Checkbox
                    label="Servizi esclusivi"
                    className="field checkbox"
                    {...getFieldProps("attributes.hasOwnBathroom")}
                />
                <Checkbox
                    label="Riscaldamento centralizzato"
                    className="field checkbox"
                    {...getFieldProps("attributes.hasCentralHeating")}
                />
                <Checkbox
                    label="Vicino alla stazione ferroviaria"
                    className="field checkbox"
                    {...getFieldProps("attributes.isCloseToTrainStation")}
                />
                <Checkbox
                    label="Comodo a linee autobus"
                    className="field checkbox"
                    {...getFieldProps("attributes.isCloseToBusLines")}
                />
                <Checkbox
                    label="Vicino al casello autostradale"
                    className="field checkbox"
                    {...getFieldProps("attributes.isCloseToHighwayExit")}
                />
            </div>
        )
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <BottomSheet
            open={open}
            onConfirm={handleSubmit}
            onCancel={onCancel}
            className={`${className}`}
            snapPoint={80}
            title={defaultValues?.id ? "Modifica stanza" : "Crea stanza"}
            loading={downloadingCoords}
            onDelete={defaultValues?.id && (() => onDelete(defaultValues))}
        >
            <div className="form">
                {renderLeft()}
                {renderRight()}
            </div>
        </BottomSheet>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_RoomsBottomSheet.propTypes = {
    className: PropTypes.string.isRequired,
    open: PropTypes.bool,
    defaultValues: PropTypes.object,
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    onDelete: PropTypes.func,
}

_RoomsBottomSheet.defaultProps = {}

// ----------------------------------------------------------------------------

const RoomsBottomSheet = styled(_RoomsBottomSheet)`
    & {
        .sheet-content {
            padding: 0px !important;
        }
        .footer {
            margin-right: 40px;
            margin-bottom: 40px;
        }

        .switch {
            width: 300px;
        }

        .form {
            display: flex;
            border-bottom: 1px solid
                ${({ theme }) => theme.colors.grayBorderDisabled};
            margin-bottom: 20px;

            .heading {
                margin: 0;
                padding: 0;
                margin-bottom: 20px;
                font-size: 18px;
                font-weight: 700;
                margin-top: 20px;
            }
        }

        .left,
        .right {
            width: 50%;
            padding-bottom: 20px;
            padding-top: 20px;

            .field {
                margin-bottom: 20px;
            }
        }
        .left {
            padding-left: 40px;
            padding-right: 40px;
        }
        .right {
            border-left: 1px solid
                ${({ theme }) => theme.colors.grayBorderDisabled};
            padding-left: 40px;
            padding-right: 40px;
        }
    }
`
// ----------------------------------------------------------------------------

export default RoomsBottomSheet
